const GA4_ID = process.env.REACT_APP_GA4_ID;
const GADS_CONVERSION = process.env.REACT_APP_GADS_CONVERSION;
const API = process.env.REACT_APP_BACKEND_URL;

const pingServer = (event) => {
  try {
    fetch(`${API}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event }),
      keepalive: true,
    }).catch((e) => console.debug("analytics ping failed:", e?.message));
  } catch (e) {
    console.debug("analytics ping error:", e?.message);
  }
};

const computeSource = () => {
  const p = new URLSearchParams(window.location.search);
  if (p.get("gclid") || (p.get("utm_source") || "").includes("google")) return "google_ads";
  const utm = (p.get("utm_source") || "").toLowerCase();
  if (utm) {
    for (const s of ["facebook", "instagram", "tiktok", "youtube", "twitter", "email"]) {
      if (utm.includes(s)) return s;
    }
    return "referral";
  }
  if (!document.referrer) return "direct";
  try {
    const h = new URL(document.referrer).hostname;
    if (h === window.location.hostname) return null;
    if (h.includes("google")) return "google";
    if (h.includes("facebook") || h.includes("fb.com")) return "facebook";
    if (h.includes("instagram")) return "instagram";
    if (h.includes("tiktok")) return "tiktok";
    if (h.includes("youtube") || h.includes("youtu.be")) return "youtube";
    if (h.includes("twitter") || h === "t.co" || h.includes("x.com")) return "twitter";
    return "referral";
  } catch {
    return "other";
  }
};

// ---------- Growth OS event pipeline (internal admin analytics — additive) ----------
const _uuid = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const getAnonId = () => {
  let id = localStorage.getItem("eh_anon_id");
  if (!id) { id = _uuid(); localStorage.setItem("eh_anon_id", id); }
  return id;
};

const getSessionId = () => {
  let id = sessionStorage.getItem("eh_growth_session");
  if (!id) { id = _uuid(); sessionStorage.setItem("eh_growth_session", id); }
  return id;
};

const getAttribution = () => {
  try {
    const cached = sessionStorage.getItem("eh_growth_attr");
    if (cached) return JSON.parse(cached);
  } catch { /* recompute */ }
  const p = new URLSearchParams(window.location.search);
  const bucketed = computeSource();
  const rawUtm = (p.get("utm_source") || "").toLowerCase() || null;
  const attr = {
    source: bucketed === "referral" && rawUtm ? rawUtm : (bucketed || "direct"),
    utm_source: p.get("utm_source") || null,
    medium: p.get("utm_medium") || null,
    campaign: p.get("utm_campaign") || null,
    content: p.get("utm_content") || null,
    term: p.get("utm_term") || null,
    referrer: document.referrer || null,
    landing_page: window.location.pathname,
  };
  try { sessionStorage.setItem("eh_growth_attr", JSON.stringify(attr)); } catch { /* ignore */ }
  return attr;
};

const deviceType = () => {
  const w = window.innerWidth;
  return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
};

let growthUserId = null;
export const setGrowthUser = (id) => { growthUserId = id || null; };

let _assessmentDone = false;
const _recentEvents = new Map();

export const growthTrack = (eventName, props = {}) => {
  try {
    if (window.location.pathname.startsWith("/admin")) return;
    const dedupeKey = `${eventName}|${props.assessment_step ?? ""}|${props.product_id ?? ""}|${window.location.pathname}`;
    const nowMs = Date.now();
    const last = _recentEvents.get(dedupeKey);
    if (last && nowMs - last < 1500) return;
    _recentEvents.set(dedupeKey, nowMs);
    fetch(`${API}/api/growth/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: eventName,
        session_id: getSessionId(),
        anonymous_id: getAnonId(),
        user_id: growthUserId,
        page_path: window.location.pathname,
        device_type: deviceType(),
        ...getAttribution(),
        ...props,
      }),
      keepalive: true,
    }).catch((e) => console.debug("growth track failed:", e?.message));
  } catch (e) {
    console.debug("growth track error:", e?.message);
  }
};

export const trackAssessmentViewed = () => { _assessmentDone = false; growthTrack("assessment_viewed"); };
export const trackAssessmentStep = (step, action) =>
  growthTrack(action === "completed" ? "assessment_step_completed" : "assessment_step_viewed", { assessment_step: step });
export const trackAssessmentAbandoned = (step) => {
  if (_assessmentDone) return;
  _assessmentDone = true;
  growthTrack("assessment_abandoned", { assessment_step: step });
};
export const trackRecommendationViewed = () => growthTrack("recommendation_viewed");
export const trackRecommendationHerbClick = (herb) =>
  growthTrack("recommendation_herb_clicked", { product_id: herb?.id, product_name: herb?.name, product_url: herb?.id ? `/herbs/${herb.id}` : null });
export const trackRecommendationStoreClick = () => growthTrack("recommendation_store_clicked");
export const trackProductViewed = (herb) =>
  growthTrack("product_viewed", { product_id: herb?.id, product_name: herb?.name, product_url: herb?.id ? `/herbs/${herb.id}` : null });
export const trackAddToCart = (productId, productName) =>
  growthTrack("add_to_cart", { product_id: productId, product_name: productName || null });
export const trackBeginCheckout = () => growthTrack("begin_checkout");

const EXTERNAL_DESTINATIONS = [
  { match: "infuso.com", name: "Shop Infuso" },
  { match: "herbalalliance.org", name: "Herbal Alliance" },
];

const installExternalClickTracking = () => {
  if (window.__ehExtClicks) return;
  window.__ehExtClicks = true;
  document.addEventListener("click", (e) => {
    const a = e.target?.closest?.("a[href]");
    if (!a) return;
    const href = a.href || "";
    const dest = EXTERNAL_DESTINATIONS.find((d) => href.includes(d.match));
    if (dest) growthTrack("external_destination_clicked", { destination_name: dest.name, destination_url: href });
  }, true);
};

const trackVisitSource = () => {
  try {
    if (sessionStorage.getItem("eh_visit_tracked")) return;
    const source = computeSource();
    if (!source) return;
    sessionStorage.setItem("eh_visit_tracked", "1");
    fetch(`${API}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event: "visit", source }),
      keepalive: true,
    }).catch((e) => console.debug("visit ping failed:", e?.message));
  } catch (e) {
    console.debug("visit tracking error:", e?.message);
  }
};

const loadGoogleTags = () => {
  if (!GA4_ID || window.gtag) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
  if (GADS_CONVERSION) {
    window.gtag("config", GADS_CONVERSION.split("/")[0]);
  }
};

export const applyCookieConsent = (choice) => {
  if (choice === "all") loadGoogleTags();
};

export const initAnalytics = () => {
  trackVisitSource();
  installExternalClickTracking();
  if (localStorage.getItem("eh_cookie_consent") === "all") loadGoogleTags();
};

export const trackPageView = (path) => {
  pingServer("page_view");
  growthTrack("page_view", { page_path: path });
  if (!window.gtag) return;
  window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
};

export const trackAssessmentStart = () => {
  pingServer("assessment_started");
  growthTrack("assessment_started");
  if (!window.gtag) return;
  window.gtag("event", "assessment_started", { event_category: "engagement" });
};

export const trackEmailSubmitted = () => {
  if (!window.gtag) return;
  window.gtag("event", "email_submitted", { event_category: "engagement" });
};

export const trackLandingCtaClick = () => {
  growthTrack("landing_cta_clicked");
  if (!window.gtag) return;
  window.gtag("event", "assessment_cta_click", { event_category: "engagement" });
};

export const trackLeadSubmitted = () => {
  growthTrack("lead_submitted");
  trackEmailSubmitted();
};

export const trackAssessmentComplete = () => {
  pingServer("assessment_completed");
  _assessmentDone = true;
  growthTrack("assessment_complete");
  if (!window.gtag) return;
  window.gtag("event", "assessment_complete", { event_category: "engagement" });
  if (GADS_CONVERSION?.includes("/")) {
    window.gtag("event", "conversion", { send_to: GADS_CONVERSION });
  }
};

export const trackPurchase = ({ transactionId, value, itemName }) => {
  growthTrack("purchase", { value, product_name: itemName, event_metadata: { transaction_id: transactionId } });
  if (!window.gtag) return;
  window.gtag("event", "purchase", {
    transaction_id: transactionId,
    value,
    currency: "GBP",
    items: [{ item_name: itemName, price: value, quantity: 1 }],
  });
};
